import { useEffect, useMemo, useState } from "react";
import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { useApiQuery } from "~/modules/filters/components/useApiQuery";
import {
    API_QUERY_BASE_URLS,
    ApiQuery,
    ApiQueryEndpoints,
    QueryResponse,
} from "~/modules/filters/types";
import { Asset } from "~/modules/assets/types";
import { AssetTable } from "~/modules/assets/components/AssetTable";
import { ChartConfig } from "~/modules/charts/types";
import { VisualizationApiToolsButton } from "~/modules/visualization/components/VisualizationApiToolsButton";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { getAssetTableColumns } from "../ChartForm/asset_table/assetTableColumns";
import { toAssetTableChartConfig } from "../ChartForm/asset_table/assetTableConfig";

// // // //

/**
 * AssetTableChartProvider
 * Fetches the configured columns of `/v4/assets` and hands the rendering off
 * to `children`.
 *
 * Sorting and paging are viewer-facing: they live in local state seeded from
 * the config's query, so a viewer can page through the results without editing
 * (or re-saving) the chart itself.
 */
export function AssetTableChartProvider(props: {
    title: string;
    description: string;
    config: ChartConfig;
    children: (props: {
        title: string;
        description: string;
        isLoading: boolean;
        tableEl: React.ReactNode;
        loadingEl: React.ReactNode;
        customControls: React.ReactNode;
    }) => React.ReactNode;
}) {
    const { title, description, config } = props;

    const assetTableConfig = useMemo(
        () => toAssetTableChartConfig(config),
        [config],
    );

    const columns = useMemo(
        () => getAssetTableColumns(assetTableConfig.table_columns),
        [assetTableConfig.table_columns],
    );

    const [query, setQuery] = useState<ApiQuery>(assetTableConfig.query);

    // Re-seed from the config whenever it changes (e.g. the Data tab's filters
    // are edited), discarding the viewer's paging so they aren't left on a page
    // that no longer exists.
    useEffect(() => {
        setQuery(assetTableConfig.query);
    }, [assetTableConfig.query]);

    const queryReq = useApiQuery({
        endpoint: ApiQueryEndpoints.AssetV4,
        query,
        options: {
            keepPreviousData: true,
        },
    });

    const loading = queryReq.isLoading || false;
    const data = queryReq.data as QueryResponse<Asset> | undefined;
    const rows = data?.results ?? [];

    const isLoading = loading && rows.length === 0;

    const customControls = (
        <div className="w-full flex items-center gap-4 justify-end">
            <VisualizationApiToolsButton
                query={query}
                apiDocsBaseUrl={API_QUERY_BASE_URLS[ApiQueryEndpoints.AssetV4]}
            />

            <DataExportProvider>
                {({ isLoading, exportCsv }) => (
                    <DownloadButton
                        disabled={isLoading}
                        onClick={() => {
                            exportCsv({
                                exportTableName: ExportTableNames.assets_v4,
                                query,
                            });
                        }}
                    />
                )}
            </DataExportProvider>
        </div>
    );

    const loadingEl = (
        <div className="flex h-[400px] items-center justify-center">
            <LogoLoadingSpinner size={75} />
        </div>
    );

    const tableEl = (
        <AssetTable
            query={query}
            onChangeQuery={setQuery}
            columns={columns}
            rows={rows}
            loading={queryReq.isFetching}
            paginationState={data?.pagination}
            analyticsTableName="chart-asset-table"
            fixedHeader={false}
            includeAdditionalNetworksRow={false}
            enablePagerPagination={false}
            flush
            watermark
        />
    );

    return (
        <>
            {props.children({
                title,
                description,
                isLoading,
                tableEl,
                loadingEl,
                customControls,
            })}
        </>
    );
}
